class ToggleVisibility extends HTMLElement {
  constructor() {
    super();
    this.targetElement = null;
    this.htmlOpenClasses = [];
  }

  connectedCallback() {
    this.init();
  }

  disconnectedCallback() {
    this.removeEventListener("click", this.handleClick);
    this.removeEventListener("keydown", this.handleKeydown);

    this.deactivateFocusTrap();

    this.targetElement = null;
  }

  init() {
    // Get the target ID from the data-target attribute
    const targetId = this.getAttribute("data-target");

    if (!targetId) {
      console.warn(
        "ToggleVisibility: No target ID specified. Use data-target attribute.",
      );
      return;
    }

    // Find the target element
    this.targetElement = document.getElementById(targetId);

    if (!this.targetElement) {
      console.warn(
        `ToggleVisibility: Target element with ID "${targetId}" not found.`,
      );
      return;
    }

    // Make the component itself behave like a button
    this.setupAsButton();

    // Classes applied to <html> while at least one target is open (space-separated)
    const classAttr =
      this.getAttribute("data-html-open-classes") || "is-toggle-visible";
    this.htmlOpenClasses = classAttr
      .split(/\s+/)
      .filter((cls) => cls.length > 0);

    // Add event listeners
    this.addEventListener("click", this.handleClick.bind(this));
    this.addEventListener("keydown", this.handleKeydown.bind(this));

    // Set initial state
    this.updateComponentState();
  }

  setupAsButton() {
    // Set ARIA role and attributes
    this.setAttribute("role", "button");
    this.setAttribute("tabindex", "0");

    // Set initial text content if not already present
    if (!this.textContent.trim()) {
      this.textContent = this.getAttribute("data-label") || "Toggle";
    }

    // Add cursor pointer for better UX
    this.style.cursor = "pointer";
  }

  handleClick(event) {
    event.preventDefault();
    this.toggle(false); // false = mouse interaction
  }

  handleKeydown(event) {
    // Handle Enter and Space key like a button
    if (event.key === "Enter" || event.key === " ") {
      event.preventDefault();
      this.toggle(true); // true = keyboard interaction
    }
  }

  toggle(isKeyboard = false) {
    if (!this.targetElement) return;

    const isHidden = this.targetElement.getAttribute("aria-hidden") === "true";

    if (isHidden) {
      // Opening the element
      this.openElement(isKeyboard);
    } else {
      // Closing the element
      this.closeElement(isKeyboard);
    }

    // Update component state
    this.updateComponentState();
  }

  openElement(isKeyboard) {
    // Store the opener reference when opening
    this.targetElement._keyboardOpener = isKeyboard ? this : null;

    // Persist classes applied for this target so any closer can remove the same set.
    this.targetElement._htmlOpenClasses = [...this.htmlOpenClasses];

    // Show the element
    this.targetElement.setAttribute("aria-hidden", "false");

    this.syncHtmlOpenClass();

    // Activate focus trap only for keyboard interactions
    if (isKeyboard) {
      this.activateFocusTrap();
    }

    document.dispatchEvent(
      new CustomEvent("toggle-visibility:opened", {
        detail: { target: this.targetElement },
        bubbles: true,
      }),
    );
  }

  closeElement(isKeyboard) {
    // Always try to deactivate focus trap (in case another toggle opened it)
    this.deactivateFocusTrap();

    // Check if focus is currently inside the target element
    const focusInsideTarget = this.targetElement.contains(
      document.activeElement,
    );

    if (focusInsideTarget) {
      if (isKeyboard && this.targetElement._keyboardOpener) {
        // Focus back to original opener for keyboard interactions
        this.targetElement._keyboardOpener.focus();
      } else {
        // For mouse interactions, just blur focus to prevent aria-hidden error
        document.activeElement.blur();
      }
    }

    // Hide the element (now safe because focus is outside)
    this.targetElement.setAttribute("aria-hidden", "true");

    this.syncHtmlOpenClass();

    // Clear tracked classes after syncing html state.
    this.targetElement._htmlOpenClasses = null;

    // Clear opener reference
    this.targetElement._keyboardOpener = null;

    document.dispatchEvent(
      new CustomEvent("toggle-visibility:closed", {
        detail: { target: this.targetElement },
        bubbles: true,
      }),
    );
  }

  updateComponentState() {
    if (!this.targetElement) return;

    const isHidden = this.targetElement.getAttribute("aria-hidden") === "true";

    // Set aria-expanded on the component itself
    this.setAttribute("aria-expanded", !isHidden);

    // Optional: Update component text based on state
    const showText = this.getAttribute("data-show-text");
    const hideText = this.getAttribute("data-hide-text");

    if (showText && hideText) {
      this.textContent = isHidden ? showText : hideText;
    }
  }

  hasOpenTarget() {
    const toggles = document.querySelectorAll("toggle-visibility[data-target]");

    for (const toggle of toggles) {
      const targetId = toggle.getAttribute("data-target");
      if (!targetId) continue;

      const target = document.getElementById(targetId);
      if (target && target.getAttribute("aria-hidden") === "false") {
        return true;
      }
    }

    return false;
  }

  syncHtmlOpenClass() {
    const isOpen = this.targetElement.getAttribute("aria-hidden") === "false";
    const classes = isOpen
      ? this.htmlOpenClasses
      : this.targetElement._htmlOpenClasses || this.htmlOpenClasses;

    if (!classes.length) return;

    // Add or remove all classes managed by this toggle
    classes.forEach((cls) => {
      if (isOpen) {
        document.documentElement.classList.add(cls);
      } else {
        document.documentElement.classList.remove(cls);
      }
    });
  }

  getFocusableElements() {
    const focusableSelectors = [
      "button",
      "[href]",
      "input",
      "select",
      "textarea",
      '[tabindex]:not([tabindex="-1"])',
      '[contenteditable="true"]',
    ].join(",");

    return Array.from(
      this.targetElement.querySelectorAll(focusableSelectors),
    ).filter((element) => {
      return (
        !element.disabled &&
        element.offsetWidth > 0 &&
        element.offsetHeight > 0 &&
        getComputedStyle(element).visibility !== "hidden"
      );
    });
  }

  activateFocusTrap() {
    // Store focus trap data on the target element so any toggle can access it
    if (!this.targetElement._focusTrap) {
      this.targetElement._focusTrap = {
        focusableElements: [],
        firstFocusableElement: null,
        lastFocusableElement: null,
        boundHandleFocusTrap: this.handleFocusTrap.bind(this),
        active: false,
      };
    }

    const trap = this.targetElement._focusTrap;
    trap.focusableElements = this.getFocusableElements();

    if (trap.focusableElements.length === 0) {
      // If no focusable elements, make the container itself focusable
      this.targetElement.setAttribute("tabindex", "-1");
      this.targetElement.focus();
      return;
    }

    trap.firstFocusableElement = trap.focusableElements[0];
    trap.lastFocusableElement =
      trap.focusableElements[trap.focusableElements.length - 1];
    trap.active = true;

    // Add event listener for focus trap
    document.addEventListener("keydown", trap.boundHandleFocusTrap);

    // Focus the first element
    trap.firstFocusableElement.focus();
  }

  deactivateFocusTrap() {
    if (
      !this.targetElement._focusTrap ||
      !this.targetElement._focusTrap.active
    ) {
      return;
    }

    const trap = this.targetElement._focusTrap;

    // Remove event listener
    document.removeEventListener("keydown", trap.boundHandleFocusTrap);

    // Clear references but keep the trap object for reuse
    trap.focusableElements = [];
    trap.firstFocusableElement = null;
    trap.lastFocusableElement = null;
    trap.active = false;
  }

  handleFocusTrap(event) {
    const trap = this.targetElement._focusTrap;
    if (!trap || !trap.active) return;

    // Handle Escape key to close
    if (event.key === "Escape") {
      event.preventDefault();
      this.toggle(true); // true = keyboard interaction
      return;
    }

    // Only handle Tab key for focus trapping
    if (event.key !== "Tab") return;

    // If no focusable elements, do nothing
    if (trap.focusableElements.length === 0) return;

    // Check if focus is outside the target element
    if (!this.targetElement.contains(document.activeElement)) {
      event.preventDefault();
      trap.firstFocusableElement.focus();
      return;
    }

    // Handle Tab navigation
    if (event.shiftKey) {
      // Shift + Tab: moving backwards
      if (document.activeElement === trap.firstFocusableElement) {
        event.preventDefault();
        trap.lastFocusableElement.focus();
      }
    } else {
      // Tab: moving forwards
      if (document.activeElement === trap.lastFocusableElement) {
        event.preventDefault();
        trap.firstFocusableElement.focus();
      }
    }
  }
}

customElements.define("toggle-visibility", ToggleVisibility);
